import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { L10N_LOCALE, L10nTranslatePipe, L10nTranslationModule, L10nTranslationService } from 'angular-l10n';
import { ConfirmComponent } from '../modals/confirm/confirm.component';
import { FileUploadHelper } from '../model/fileupload-helper';

export interface ProfilePictureChange {
  file: File | null;
  remove: boolean;
}

@Component({
  selector: 'app-profile-picture',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, L10nTranslatePipe, L10nTranslationModule],
  templateUrl: './profile-picture.component.html',
  styleUrl: './profile-picture.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfilePictureComponent {
  private readonly dialog = inject(MatDialog);
  private readonly translation = inject(L10nTranslationService);
  readonly locale = inject(L10N_LOCALE);

  readonly imageUrl = input<string | undefined>();
  readonly name = input<string>('');

  readonly changed = output<ProfilePictureChange>();

  readonly newFile = signal<File | null>(null);
  readonly removePicture = signal(false);
  readonly profilePictureUrl = signal<string>('/resources/images/recipients_icon.png');

  readonly hasProfilePicture = computed(() => !!this.newFile() || (!!this.imageUrl() && !this.removePicture()));

  constructor() {
    effect(() => {
      const url = this.imageUrl();
      if (!this.newFile()) {
        this.profilePictureUrl.set(url || '/resources/images/recipients_icon.png');
      }
    });
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.newFile.set(file);
      this.removePicture.set(false);
      FileUploadHelper.convertToDataUrl(file)
        .then((dataUrl) => this.profilePictureUrl.set(dataUrl))
        .catch((err) => console.error('Error converting file to data URL:', err));
      this.changed.emit({ file, remove: false });
    }
  }

  onDeleteImage(): void {
    const dialog = this.dialog.open(ConfirmComponent, {
      width: '500px',
      data: {
        title: this.translation.translate('Contacts.Management.Dialog.RemoveProfilePictureTitle'),
        message: this.translation.translate('Contacts.Management.Dialog.RemoveProfilePictureMessage'),
      },
    });

    dialog.afterClosed().pipe(take(1)).subscribe((result) => {
      if (result) {
        this.newFile.set(null);
        this.removePicture.set(true);
        this.profilePictureUrl.set('/resources/images/recipients_icon.png');
        this.changed.emit({ file: null, remove: true });
      }
    });
  }

  reset(): void {
    this.newFile.set(null);
    this.removePicture.set(false);
    this.profilePictureUrl.set(this.imageUrl() ?? '/resources/images/recipients_icon.png');
  }
}
